import React, { createContext, useContext, useState, useCallback } from "react";
import { FaCheckCircle, FaTimes, FaExclamationCircle, FaInfoCircle } from "react-icons/fa";

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "success") => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prevToasts) => [...prevToasts, { id, message, type }]);

    setTimeout(() => {
      removeToast(id);
    }, 3000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={() => removeToast(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem = ({ toast, onRemove }) => {
  const getIcon = () => {
    switch (toast.type) {
      case "success": return <FaCheckCircle />;
      case "error": return <FaTimes />;
      case "warning": return <FaExclamationCircle />;
      case "info": return <FaInfoCircle />;
      default: return <FaCheckCircle />;
    }
  };

  const getBgColor = () => {
    switch (toast.type) {
      case "success": return "bg-emerald-500";
      case "error": return "bg-red-500";
      case "warning": return "bg-amber-500";
      case "info": return "bg-blue-500";
      default: return "bg-emerald-500";
    }
  };

  return (
    <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-2xl ${getBgColor()} text-white text-xs font-bold animate-in slide-in-from-right-5 duration-300 pointer-events-auto min-w-[200px]`}>
      <div className="text-sm">{getIcon()}</div>
      <div className="flex-1">{toast.message}</div>
      <button onClick={onRemove} className="hover:opacity-70 transition-opacity ml-2">
        <FaTimes size={12} />
      </button>
    </div>
  );
};
